/**
 * Provider Managed Services types (New Backend)
 *
 * Backend: accounts/views.py, accounts/schema.py, orders/schema.py
 * Contract: wtdcare_backend/docs/features/provider-managed-services.md
 *
 * The authoritative set is ProfileProvider.capable_services. Dispatch gates on it
 * fail-closed, so a provider only receives dispatches for the services enabled here.
 *
 * Unit warning — duration crosses three representations:
 *   REST GET/PUT  estimated_duration        → float SECONDS
 *   GraphQL       estimatedDuration         → Int MINUTES
 *   REST POST     estimated_duration        → DRF duration string "HH:MM:SS"
 * Convert at each boundary; never let a bare number cross layers.
 */

// ============================================================================
// Authorized services — REST GET/PUT /api/providers/<uuid>/services/
// ============================================================================

export interface ProviderAuthorizedService {
  id: string
  name: string
  /** Client-facing display name; `name` is the internal one. May be empty. */
  label: string
  /** Decimal serialized as a STRING by DRF, or null. Never do arithmetic on it directly. */
  base_price: string | null
  /** Float SECONDS, or null. */
  estimated_duration: number | null
}

export interface ProviderServicesResponse {
  success: boolean
  provider_id: string
  services: ProviderAuthorizedService[]
}

export interface SetProviderServicesInput {
  /** Full replacement set — this is not a delta. An empty array clears all services. */
  service_template_ids: string[]
}

// ============================================================================
// Service catalog — GraphQL serviceTemplates(isActive: true, limit: Int)
// ============================================================================

/**
 * The catalog item is exactly the order-flow ServiceTemplate — reuse it rather than
 * maintaining a parallel shape. Note `estimatedDuration` here is Int MINUTES.
 */
export type { ServiceTemplate as ServiceCatalogItem } from '@/services/orderApi'

// ============================================================================
// Proposals — GraphQL myServiceProposals + REST POST /api/providers/service-proposals/
// ============================================================================

export type ServiceProposalStatus = 'pending' | 'approved' | 'rejected'

export interface ServiceProposal {
  id: string
  serviceName: string
  description?: string | null
  status: ServiceProposalStatus
  statusDisplay?: string | null
  /** Empty string when not rejected. */
  rejectionReason?: string | null
  reviewedAt?: string | null
  createdAt: string
  /** Decimal as STRING, or null. */
  proposedPrice?: string | null
  estimatedDurationSeconds?: number | null
  providerId?: string | null
  categoryName?: string | null
  /** Set only once Operations approves and a ServiceTemplate is created/reused. */
  resultingServiceTemplateId?: string | null
}

export interface SubmitServiceProposalInput {
  service_name: string
  description?: string
  /** Decimal as string, e.g. "250.00". */
  proposed_price: string
  /** DRF duration format, e.g. "01:30:00". */
  estimated_duration: string
  category_id?: string
}

export interface SubmitServiceProposalResponse {
  success: boolean
  proposal: {
    id: string
    service_name: string
    status: ServiceProposalStatus
  }
}

// ============================================================================
// Helpers
// ============================================================================

/** Minutes → DRF duration string ("90" → "01:30:00"). */
export function minutesToDurationString(minutes: number): string {
  const safe = Math.max(0, Math.floor(minutes))
  const hh = String(Math.floor(safe / 60)).padStart(2, '0')
  const mm = String(safe % 60).padStart(2, '0')
  return `${hh}:${mm}:00`
}

/** Float seconds → whole minutes, for display alongside catalog durations. */
export function secondsToMinutes(seconds: number | null | undefined): number | null {
  if (seconds == null) return null
  return Math.round(seconds / 60)
}
